<template>
  <div
    class="brand-wrapper group relative flex w-48 flex-col space-y-2"
    :data-cy="`widget-branding-${template.id}`"
  >
    <icon-base
      :icon="IconDeleteWhite"
      class="edit-btn absolute top-4 right-9 z-100 transform cursor-pointer transition-all hover:scale-110"
      :class="{
        editable: !editDisabled,
      }"
      @click="handleTemplateDelete"
    />
    <icon-base
      :icon="IconEditWhite"
      class="edit-btn absolute top-4 right-2 z-100 transform cursor-pointer transition-all hover:scale-110"
      :class="{
        editable: !editDisabled,
      }"
      @click="handleTemplateClick(template.id)"
    />
    <div
      class="relative h-44 w-48 rounded-sm border bg-white"
      :class="{
        'cursor-pointer': !editDisabled,
      }"
      @click="handleTemplateClick(template.id)"
    >
      <div
        v-if="!editDisabled"
        class="absolute top-0 left-0 z-50 h-full w-full rounded-sm bg-black opacity-0 group-hover:opacity-50"
      />
      <div
        class="absolute left-1/2 z-50 h-7 w-7 -translate-x-1/2 -translate-y-1/2 transform overflow-hidden rounded-full border-3 border-gray-200 bg-white"
      >
        <img :src="template.logo?.file_url" loading="lazy" />
      </div>
      <div class="background m-1 flex h-18 flex-col items-center rounded-md">
        <div class="mt-5 h-3 w-36 rounded-xs bg-white opacity-30" />
        <div class="mt-5 h-0-5 w-36 rounded-full bg-white" />
        <div />
      </div>
      <div class="mt-4 flex flex-col items-center space-y-3">
        <div class="flex w-36 justify-between">
          <div class="h-3 w-16 rounded-xs border" />
          <div class="h-3 w-16 rounded-xs border" />
        </div>
        <div class="h-3 w-36 rounded-xs border" />
        <div class="primary-btn h-4 w-36 rounded-xs" />
      </div>
    </div>

    <label class="flex cursor-pointer">
      <input
        type="radio"
        :checked="template.id === modelValue?.id"
        class="mt-0-25 mr-2 inline-block shrink-0 cursor-pointer"
        @click="handleSelect"
      />
      <span class="template-name text-gray-600">
        {{ template.name }}
      </span>
    </label>
  </div>
</template>

<script setup lang="ts">
import { useRouter } from "vue-router";

import { ROUTE_WIDGET_BRANDING_TEMPLATE } from "@/router/routes";
import useWidgetTemplatesStore from "@/stores/widgetTemplates";

import IconDeleteWhite from "@/components/icons/IconDeleteWhite.vue";
import IconEditWhite from "@/components/icons/IconEditWhite.vue";

import type { IWidgetTemplate } from "@/models/widgetTemplates";
import type { PropType } from "vue";

const emit = defineEmits(["update:modelValue"]);

const props = defineProps({
  template: {
    type: Object as PropType<IWidgetTemplate>,
    required: true,
  },
  modelValue: {
    type: Object as PropType<IWidgetTemplate | null>,
    required: true,
  },
  editDisabled: {
    type: Boolean,
    default: false,
  },
});
const router = useRouter();
const widgetTemplatesStore = useWidgetTemplatesStore();

const handleSelect = (): void => {
  emit("update:modelValue", props.template);
};

const handleTemplateClick = (templateId: number): void => {
  if (props.editDisabled) {
    return;
  }
  widgetTemplatesStore.active = props.template;
  router.push({
    name: ROUTE_WIDGET_BRANDING_TEMPLATE,
    params: { id: templateId },
  });
};
const handleTemplateDelete = (): void => {
  widgetTemplatesStore.delete(props.template);
};
</script>

<style scoped>
@reference "@/assets/tailwind.css";

.background {
  background-color: v-bind("props.template.colour");
}
.primary-btn {
  background-color: v-bind("props.template.button_colour");
}
.brand-wrapper:hover > .editable,
.editable:hover {
  @apply opacity-100;
}
.edit-btn {
  @apply opacity-0;
}

.brand-wrapper:hover .template-name {
  word-break: break-word;
}

.brand-wrapper:not(:hover) .template-name {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style>
